/* AdminLogin — Phase 4 couture stage door.
   Two-column: left full-bleed editorial photograph; right dark charcoal panel
   with hairline-underline form. Preserves all existing auth, Google OAuth,
   forgot-password, and remember-me logic. */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import {
  ChapterEyebrow, DisplayHeadline, GoldRule, GoldFoilButton, FilmGrain,
} from "@/components/editorial";
import PasswordInput from "@/components/PasswordInput";
import usePageMeta from "@/hooks/usePageMeta";

function GoogleGlyph({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 5.0c1.6 0 3.1.6 4.2 1.6l3.1-3.1C17.4 1.7 14.9.6 12 .6 7.4.6 3.4 3.2 1.4 7.0l3.6 2.8C6.1 6.9 8.8 5.0 12 5.0z"/>
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5c-.3 1.5-1.1 2.7-2.4 3.5l3.6 2.8c2.1-2 3.3-4.9 3.3-8.4z"/>
      <path fill="#FBBC05" d="M5.0 14.2c-.3-.8-.5-1.7-.5-2.6s.2-1.8.5-2.6L1.4 6.2C.5 7.9 0 9.9 0 12.0s.5 4.1 1.4 5.8l3.6-3.6z"/>
      <path fill="#34A853" d="M12 23.4c3.2 0 5.9-1.1 7.9-2.9l-3.6-2.8c-1 .7-2.3 1.1-4.3 1.1-3.2 0-5.9-1.9-6.9-4.8L1.4 17.8c2 3.8 6.0 5.6 10.6 5.6z"/>
    </svg>
  );
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("allure_remember_email") || "");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem("allure_remember_me") !== "false");
  const [loading, setLoading] = useState(false);

  usePageMeta({
    title: "Sign in — Lumetier Studio Manager",
    description: "Sign in to your Lumetier studio dashboard to manage bookings, galleries, contracts, and clients.",
    canonical: "https://lumetiercreatives.com/admin/login",
    noIndex: true,
  });

  useEffect(() => { if (user) nav("/admin", { replace: true }); }, [user, nav]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  }

  function continueWithGoogle() {
    localStorage.setItem("allure_remember_me", rememberMe ? "true" : "false");
    const redirectUrl = `${window.location.origin}/admin/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[3fr_2fr] bg-[color:var(--charcoal)] text-[color:var(--cream)]" data-testid="admin-login-page">
      {/* LEFT — editorial photograph (hidden on mobile, becomes top band) */}
      <aside className="relative hidden lg:block overflow-hidden">
        <img
          src="/allure/studio-interior.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A09]/35 via-transparent to-[#0E0D0C]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E0D0C]/55 via-transparent to-transparent" />
        <FilmGrain opacity={0.05} tone="cool" />
        <div className="absolute top-8 left-8">
          <p className="ed-eyebrow text-[color:var(--gold-foil)]">LUMETIER · STUDIO INTERIOR · VOL. 01</p>
        </div>
        <div className="absolute bottom-8 left-8 right-8">
          <p className="font-serif italic text-2xl md:text-3xl text-[color:var(--cream)]/85 max-w-xl leading-snug">
            Welcome back to the room where the work gets quietly made.
          </p>
        </div>
      </aside>

      {/* RIGHT — dark charcoal panel */}
      <section className="relative flex items-center justify-center px-6 py-14">
        <FilmGrain opacity={0.04} tone="cool" />
        <div className="relative w-full max-w-md">
          {/* Wordmark */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center gap-3">
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M10 1 L11.6 7.4 L18 8.4 L13.2 12.4 L14.6 19 L10 15.6 L5.4 19 L6.8 12.4 L2 8.4 L8.4 7.4 Z" fill="var(--gold-base)" />
              </svg>
              <span className="font-serif text-2xl text-[color:var(--cream)]" style={{ letterSpacing: "0.42em" }}>LUMETIER</span>
            </div>
          </div>

          <div className="text-center mb-9">
            <ChapterEyebrow label="Stage Door" />
            <GoldRule width="2rem" align="center" className="mt-3 mb-5" />
            <DisplayHeadline as="h1" size="section" align="center" tone="charcoal" className="text-[color:var(--cream)]">
              Welcome <em>back</em>.
            </DisplayHeadline>
            <p className="font-serif italic text-lg mt-4 text-[color:var(--cream)]/65">Sign in to your studio.</p>
          </div>

          <form onSubmit={submit} className="space-y-7">
            <label className="block">
              <span className="ed-eyebrow text-[color:var(--gold-foil)] mb-3 block">Email</span>
              <input
                type="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="w-full bg-transparent border-0 border-b border-[color:var(--cream)]/30 focus:border-[color:var(--gold-foil)] py-2.5 outline-none text-[color:var(--cream)] placeholder:text-[color:var(--cream)]/30 text-base transition-colors"
              />
            </label>
            <label className="block">
              <div className="flex items-baseline justify-between mb-3">
                <span className="ed-eyebrow text-[color:var(--gold-foil)]">Password</span>
                <Link to="/admin/forgot-password" className="ed-eyebrow text-[color:var(--gold-base)] hover:text-[color:var(--gold-foil)]">forgot?</Link>
              </div>
              <PasswordInput
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                inputClassName="w-full bg-transparent border-0 border-b border-[color:var(--cream)]/30 focus:border-[color:var(--gold-foil)] py-2.5 outline-none text-[color:var(--cream)] placeholder:text-[color:var(--cream)]/30 text-base transition-colors"
              />
            </label>
            <label className="flex items-center gap-2 text-[color:var(--cream)]/70">
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="accent-[color:var(--gold-base)]" />
              <span className="text-sm">Remember me</span>
            </label>
            <GoldFoilButton type="submit" size="lg" disabled={loading} data-testid="admin-login-submit" className="w-full justify-center">
              {loading ? "Signing in…" : "Sign in"}
            </GoldFoilButton>
          </form>

          <div className="my-7 flex items-center gap-4">
            <span className="flex-1 h-px bg-[color:var(--cream)]/15" />
            <span className="ed-eyebrow text-[color:var(--cream)]/45">or continue with</span>
            <span className="flex-1 h-px bg-[color:var(--cream)]/15" />
          </div>
          <button
            type="button"
            onClick={continueWithGoogle}
            className="w-full inline-flex items-center justify-center gap-3 py-3 border border-[color:var(--cream)]/25 hover:border-[color:var(--gold-foil)]/60 transition-colors text-[color:var(--cream)]/90 ed-eyebrow"
            data-testid="google-login-btn"
          >
            <GoogleGlyph className="w-4 h-4" />
            Google
          </button>

          <p className="text-center mt-9 font-serif italic text-[color:var(--cream)]/55">
            New here? <Link to="/lumetier/onboard" className="text-[color:var(--gold-base)] hover:text-[color:var(--gold-foil)] underline-offset-4 hover:underline">Request an invite →</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
